import { debounce } from 'lodash';
import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  fetchGooglePlacesDetails,
  type LocationAnswer,
} from '@anyroad/google-maps';
import { useGooglePlacesLocation } from '@anyroad/hooks';
import { AsyncSelect } from '@anyroad/select';

const DEBOUNCE_MS = 50;
const MIN_QUERY_LENGTH = 3;
const apiKey = process.env.GOOGLE_MAPS_API_KEY ?? '';

type LocationProps = {
  label: string;
  subtext: string;
  position: number;
  value?: LocationAnswer | null;
  onChange: (position: number, value: LocationAnswer) => void;
  error?: string | undefined;
};

export const Location = ({
  label,
  subtext,
  position,
  value,
  onChange,
  error,
}: LocationProps) => {
  const { formatMessage } = useIntl();
  const rawInputRef = useRef('');
  const { loadOptions: loadOptionsFromHook } = useGooglePlacesLocation({
    apiKey,
    types: ['geocode'],
  });

  const debouncedLoad = useMemo(
    () => debounce(loadOptionsFromHook, DEBOUNCE_MS),
    [loadOptionsFromHook]
  );

  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

  const loadOptions = useMemo(
    () => (inputValue: string) => {
      rawInputRef.current = inputValue;
      if (inputValue.trim().length < MIN_QUERY_LENGTH) {
        return Promise.resolve([]);
      }
      return Promise.resolve(debouncedLoad(inputValue)).then(
        (result) => result ?? []
      );
    },
    [debouncedLoad]
  );

  const handleChange = useCallback(
    async (selectedValue: string) => {
      if (!selectedValue) return;
      if (selectedValue.trim().includes(' ')) return;
      const rawAddress = rawInputRef.current || selectedValue;
      try {
        const locationAnswer = await fetchGooglePlacesDetails({
          apiKey,
          placeId: selectedValue,
          rawAddress,
        });
        if (locationAnswer) {
          onChange(position, locationAnswer);
          return;
        }
      } catch {}
      const fallback: LocationAnswer = {
        raw_address: rawAddress,
        validation_status: 'unvalidated',
      };
      onChange(position, fallback);
    },
    [onChange, position]
  );

  return (
    <AsyncSelect
      errorMessage={error ?? undefined}
      label={label}
      loadOptions={loadOptions}
      placeholder={formatMessage({
        defaultMessage: 'Start typing your address...',
      })}
      subtext={subtext}
      value={
        value
          ? {
              label: value.formatted_address ?? value.raw_address,
              value: value.place_id ?? '',
              cityState: value.address_components?.city
                ? [
                    value.address_components.city,
                    value.address_components.state_province_code,
                    value.address_components.country_code,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : '',
            }
          : undefined
      }
      onChange={handleChange}
    />
  );
};
